// ============================================================
// ESTABLISHMENTS COMPONENT - WITH PAGINATION
// File: src/components/Establishments.js
// ============================================================

import React, { useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { supabase } from '../supabaseClient'
import { useAuth } from '../AuthContext'
import { useOrganization } from '../hooks/useOrganization'
import { useEstablishments } from '../hooks/useEstablishments'
import { useAgents } from '../hooks/useAgents'
import InspectionHistory from './InspectionHistory'
import MapView from './MapView'
import NewEstablishmentForm from './NewEstablishmentForm'
import EditEstablishmentForm from './EditEstablishmentForm'
import ConfirmDialog from './ConfirmDialog'
import PromptDialog from './PromptDialog'
import { TableSkeleton } from './Skeleton'
import EstablishmentCard from './EstablishmentCard'
import { useTheme } from '../ThemeContext'
import { useLayout } from '../LayoutContext'
import { Badge } from './UI'
import { Building2, List, Map, LayoutGrid, Pencil, Archive } from 'lucide-react'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'

const SORT_STORAGE_KEY = 'abc_inspection_sort_preferences'

export default function Establishments({ onNewInspection, onFilterChange, onAgentFilterChange }) {
  const { user } = useAuth()
  const { colors, darkMode } = useTheme()
  const { isModernLayout } = useLayout()
  const { organization } = useOrganization()
  const idLabel = organization?.settings?.identifier_label || 'MXB #'

  // Modern layout color helpers — matches dashboard palette
  const mGlassBg     = darkMode ? '#122036'  : '#ffffff'
  const mGlassBorder = darkMode ? 'rgba(125,170,218,0.18)' : 'rgba(54,93,130,0.16)'
  const mGlassShadow = darkMode ? '0 22px 60px rgba(0,0,0,0.28)' : '0 18px 50px rgba(31,66,104,0.12)'
  const mPrimary     = darkMode ? '#edf5ff'  : '#102033'
  const mSecondary   = darkMode ? '#91a9c2'  : '#506b87'
  const mMuted       = darkMode ? '#5f7893'  : '#7891aa'
  const mAccent      = '#3d6494'
  const mRowHover    = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
  const mRowBorder   = darkMode ? 'rgba(125,170,218,0.12)' : 'rgba(54,93,130,0.1)'
  const mTheadBg     = darkMode ? 'rgba(125,170,218,0.06)' : 'rgba(54,93,130,0.04)'
  const mAgentBg     = darkMode ? 'rgba(61,100,148,0.18)'  : 'rgba(61,100,148,0.08)'
  const queryClient = useQueryClient()
  const pendingRiskIds = React.useRef(new Set())

  // Establishment type toggle (must be declared before useEstablishments)
  const [establishmentType, setEstablishmentType] = useState('mxb') // 'mxb' or 'beer_wine'

  // Use React Query hook for data fetching (only active establishments)
  // Pass organization ID to filter data correctly for super_admins
  const { data, isLoading, error} = useEstablishments(false, organization?.id, establishmentType)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const establishments = data?.establishments || []
  const cities = data?.cities || []

  // Fetch all active agents for the dropdown (not just those with establishments)
  const { agents: agentsList } = useAgents({ activeOnly: true, role: ['inspector', 'admin'] })
  const agents = agentsList.map(a => `${a.first_name} ${a.last_name}`).sort()


  const [selectedEstablishment, setSelectedEstablishment] = useState(null)

  // Actions dropdown state
  const [openActionsDropdown, setOpenActionsDropdown] = useState(null)

  // View toggle
  const [viewMode, setViewMode] = useState('table') // 'table', 'cards', or 'map'

  // Responsive layout detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openActionsDropdown) {
        setOpenActionsDropdown(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [openActionsDropdown])
  
  // New establishment form
  const [showNewForm, setShowNewForm] = useState(false)
  
  // Edit establishment form
  const [showEditForm, setShowEditForm] = useState(false)
  const [establishmentToEdit, setEstablishmentToEdit] = useState(null)

  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, data: null })
  const [promptDialog, setPromptDialog] = useState({ isOpen: false, data: null })

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const RECORDS_PER_PAGE = 200
  // Phones: a compact list that grows with "Show more" instead of numbered pages
  const MOBILE_PAGE = 50
  const [mobileVisible, setMobileVisible] = useState(MOBILE_PAGE)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Route planning state
  const [routePlanningMode, setRoutePlanningMode] = useState(false)
  const [selectedForRoute, setSelectedForRoute] = useState(new Set())
  const [routeStart, setRouteStart] = useState('')
  const [routeEnd, setRouteEnd] = useState('')
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false)

  const toggleRouteSelection = (estId, e) => {
    e.stopPropagation()
    setSelectedForRoute(prev => {
      const next = new Set(prev)
      if (next.has(estId)) {
        next.delete(estId)
      } else {
        next.add(estId)
        // Auto-populate start address with first selected establishment
        if (next.size === 1) {
          const est = establishments.find(es => es.id === estId)
          if (est) setRouteStart(`${est.address}, ${est.city}, NC ${est.zip}`)
        }
      }
      return next
    })
  }

  const openRoute = () => {
    if (!routeStart.trim()) {
      toast.error('Please enter a start address')
      return
    }
    if (selectedForRoute.size === 0) {
      toast.error('Please select at least one establishment')
      return
    }
    setIsCalculatingRoute(true)
    const selectedEsts = establishments.filter(e => selectedForRoute.has(e.id))
    const stops = selectedEsts.map(e =>
      e.latitude && e.longitude
        ? `${e.latitude},${e.longitude}`
        : encodeURIComponent(`${e.address}, ${e.city}, NC ${e.zip}`)
    )
    const origin = encodeURIComponent(routeStart.trim())
    const destination = routeEnd.trim() ? encodeURIComponent(routeEnd.trim()) : origin
    const waypoints = stops.join('/')
    const url = `https://www.google.com/maps/dir/${origin}/${waypoints}/${destination}`
    window.location.href = url
    setIsCalculatingRoute(false)
    setRoutePlanningMode(false)
    setSelectedForRoute(new Set())
    setRouteStart('')
    setRouteEnd('')
  }

  // Filter states
  const [riskOverrides, setRiskOverrides] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [agentFilter, setAgentFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentUserName, setCurrentUserName] = useState(null)
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasUserChangedFilter, setHasUserChangedFilter] = useState(false)

  // Expose filter setter to parent
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({ setStatusFilter })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Notify parent when agent filter changes — but only after the user role is known,
  // to avoid the initial '' value overriding the inspector-scoped filter App.js already set.
  useEffect(() => {
    if (!currentUserRole) return
    if (onAgentFilterChange) {
      onAgentFilterChange(agentFilter)
    }
  }, [agentFilter, currentUserRole, onAgentFilterChange])

  // Fetch current user's name and role on mount
  useEffect(() => {
    async function fetchCurrentUser() {
      if (!user) return

      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, role')
          .eq('email', user.email)
          .single()

        if (error) throw error

        if (data) {
          const fullName = `${data.first_name} ${data.last_name}`
          setCurrentUserName(fullName)
          setCurrentUserRole(data.role)

          // Set default filter only if user hasn't manually changed it
          if (isInitialLoad && !hasUserChangedFilter) {
            // Super admins and regular admins default to "All Agents"
            if (data.role === 'super_admin' || data.role === 'admin') {
              setAgentFilter('')
            } else {
              // Regular inspectors default to their own establishments
              setAgentFilter(fullName)
              // When filtering to current user on initial load, always sort by oldest inspection first
              // This ensures mobile users see establishments needing inspection at the top
              setSortField('last_inspection')
              setSortDirection('asc')
            }
            setIsInitialLoad(false)
          }
        }
      } catch (error) {
        console.error('Error fetching current user:', error)
      }
    }

    fetchCurrentUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInitialLoad])

  // Load sort preferences from localStorage on mount
  const loadSortPreferences = () => {
    try {
      const saved = localStorage.getItem(SORT_STORAGE_KEY)
      if (saved) {
        const { field, direction } = JSON.parse(saved)
        return { field, direction }
      }
    } catch (err) {
      console.error('Error loading sort preferences:', err)
    }
    // Default sort: oldest inspection date first (most overdue at top)
    return { field: 'last_inspection', direction: 'asc' }
  }

  const initialSort = loadSortPreferences()
  
  // Sort state
  const [sortField, setSortField] = useState(initialSort.field)
  const [sortDirection, setSortDirection] = useState(initialSort.direction)

  // Save sort preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({
        field: sortField,
        direction: sortDirection
      }))
    } catch (err) {
      console.error('Error saving sort preferences:', err)
    }
  }, [sortField, sortDirection])

  const getLastInspectionDate = useCallback((inspections, riskLevel = 'low') => {
    const allInspections = inspections || []
    const realInspections = allInspections.filter(i => !i.is_new_opening)
    const newOpeningInspections = allInspections.filter(i => i.is_new_opening)

    // No inspections at all
    if (allInspections.length === 0) {
      return { date: 'Never', status: 'never', isNewOpeningOnly: false }
    }

    // Determine if this establishment only has new opening inspections (no physical inspections)
    const isNewOpeningOnly = realInspections.length === 0 && newOpeningInspections.length > 0

    // Use the most recent inspection date (including new openings for date/status calculation)
    const lastInspectionDateString = allInspections
      .map(i => i.inspection_date)
      .sort()
      .reverse()[0]

    // Parse the date string (YYYY-MM-DD) without timezone conversion
    const [year, month, day] = lastInspectionDateString.split('-').map(Number)
    const lastInspection = new Date(year, month - 1, day)

    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset time to midnight for accurate day calculation

    const daysSince = Math.floor((today - lastInspection) / (1000 * 60 * 60 * 24))

    let status = 'current'
    if (riskLevel === 'high') {
      if (daysSince > 90) status = 'overdue'
      else if (daysSince > 60) status = 'due_soon'
    } else if (riskLevel === 'medium') {
      if (daysSince > 180) status = 'overdue'
      else if (daysSince > 120) status = 'due_soon'
    } else {
      if (daysSince > 365) status = 'overdue'
      else if (daysSince > 180) status = 'due_soon'
    }

    return {
      date: lastInspection.toLocaleDateString(),
      status,
      isNewOpeningOnly
    }
  }, [])

  // Use useMemo instead of useCallback + useEffect to avoid infinite loops
  const filteredEstablishments = React.useMemo(() => {
    let filtered = [...establishments]

    // Search term (searches trade name, MXB number, address)
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(est =>
        est.trade_name?.toLowerCase().includes(search) ||
        est.mxb_number?.toLowerCase().includes(search) ||
        est.address?.toLowerCase().includes(search)
      )
    }

    // City filter
    if (cityFilter) {
      filtered = filtered.filter(est => est.city === cityFilter)
    }

    // Agent filter
    if (agentFilter) {
      if (agentFilter === '__unassigned__') {
        // Filter for establishments with no assigned agent
        filtered = filtered.filter(est => !est.users)
      } else {
        filtered = filtered.filter(est =>
          est.users && `${est.users.first_name} ${est.users.last_name}` === agentFilter
        )
      }
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(est => {
        const { status } = getLastInspectionDate(est.inspections, est.risk_level)
        return status === statusFilter
      })
    }

    // Sorting
    filtered.sort((a, b) => {
      // Priority establishments always float to top
      if (a.priority_inspection && !b.priority_inspection) return -1
      if (!a.priority_inspection && b.priority_inspection) return 1

      let aVal, bVal

      switch (sortField) {
        case 'mxb_number':
          aVal = a.mxb_number || ''
          bVal = b.mxb_number || ''
          break
        case 'trade_name':
          aVal = a.trade_name?.toLowerCase() || ''
          bVal = b.trade_name?.toLowerCase() || ''
          break
        case 'address':
          aVal = a.address?.toLowerCase() || ''
          bVal = b.address?.toLowerCase() || ''
          break
        case 'city':
          aVal = a.city?.toLowerCase() || ''
          bVal = b.city?.toLowerCase() || ''
          break
        case 'zip':
          aVal = a.zip || ''
          bVal = b.zip || ''
          break
        case 'agent':
          aVal = a.users ? `${a.users.first_name} ${a.users.last_name}`.toLowerCase() : 'zzz'
          bVal = b.users ? `${b.users.first_name} ${b.users.last_name}`.toLowerCase() : 'zzz'
          break
        case 'status':
          const statusOrder = { current: 1, due_soon: 2, overdue: 3, never: 4 }
          aVal = statusOrder[getLastInspectionDate(a.inspections, a.risk_level).status] || 5
          bVal = statusOrder[getLastInspectionDate(b.inspections, b.risk_level).status] || 5
          break
        case 'risk':
          const riskOrder = { high: 1, medium: 2, low: 3 }
          aVal = riskOrder[riskOverrides[a.id] ?? a.risk_level ?? 'low'] || 3
          bVal = riskOrder[riskOverrides[b.id] ?? b.risk_level ?? 'low'] || 3
          break
        case 'last_inspection':
          // Use all inspections including new openings for date sorting
          const aInspections = a.inspections || []
          const bInspections = b.inspections || []
          aVal = aInspections.length > 0 ? Math.max(...aInspections.map(i => new Date(i.inspection_date).getTime())) : 0
          bVal = bInspections.length > 0 ? Math.max(...bInspections.map(i => new Date(i.inspection_date).getTime())) : 0
          break
        default:
          aVal = a.mxb_number || ''
          bVal = b.mxb_number || ''
      }

      // Compare values
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments, searchTerm, cityFilter, agentFilter, statusFilter, sortField, sortDirection, getLastInspectionDate])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
    setMobileVisible(MOBILE_PAGE)
  }, [searchTerm, cityFilter, agentFilter, statusFilter, establishmentType])

  const clearFilters = () => {
    setSearchTerm('')
    setCityFilter('')
    setStatusFilter('')
    // Reset agent filter based on role
    if (currentUserRole === 'super_admin' || currentUserRole === 'admin') {
      setAgentFilter('') // Super admins and admins default to All Agents
    } else if (currentUserName) {
      setAgentFilter(currentUserName) // Regular inspectors default to their own
    } else {
      setAgentFilter('')
    }
  }

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleEdit = (establishment, e) => {
    e.stopPropagation() // Prevent row click from triggering
    setEstablishmentToEdit(establishment)
    setShowEditForm(true)
  }

  const handleArchive = (establishment, e) => {
    e.stopPropagation() // Prevent row click from triggering

    setConfirmDialog({
      isOpen: true,
      data: {
        establishment,
        onConfirm: async () => {
          setConfirmDialog({ isOpen: false, data: null })
          await performArchive(establishment)
        }
      }
    })
  }

  const handleRiskClick = (est, e) => {
    e.stopPropagation()
    const cycle = { low: 'medium', medium: 'high', high: 'low' }
    const currentLevel = riskOverrides[est.id] ?? est.risk_level ?? 'low'
    const newLevel = cycle[currentLevel]
    // riskOverrides is intentionally NOT in filteredEstablishments memo deps,
    // so this update shows the new badge without triggering a re-sort
    setRiskOverrides(prev => ({ ...prev, [est.id]: newLevel }))
    // Mark this id so the real-time handler patches the cache silently
    pendingRiskIds.current.add(est.id)
    supabase.from('establishments')
      .update({ risk_level: newLevel, risk_manual_override: true })
      .eq('id', est.id)
      .then(({ error }) => {
        if (error) {
          pendingRiskIds.current.delete(est.id)
          setRiskOverrides(prev => { const n = { ...prev }; delete n[est.id]; return n })
        }
        // On success: real-time event patches the cache without re-sorting
      })
  }

  const performArchive = async (establishment) => {
    try {
      const { error } = await supabase
        .from('establishments')
        .update({ active: false, priority_inspection: false, priority_reason: null, priority_set_at: null, priority_set_by: null })
        .eq('id', establishment.id)

      if (error) throw error

      logAuditEvent(AUDIT_ACTIONS.ESTABLISHMENT_ARCHIVED, {
        establishment_id: establishment.id,
        mxb_number: establishment.mxb_number,
        trade_name: establishment.trade_name
      }).catch(() => {})
      toast.success(`"${establishment.trade_name}" has been archived successfully.`)
      queryClient.invalidateQueries({ queryKey: ['establishments'] })
    } catch (error) {
      console.error('Error archiving establishment:', error)
      toast.error('Failed to archive establishment: ' + error.message)
    }
  }

  // eslint-disable-next-line no-unused-vars
  const handleRestore = (establishment, e) => {
    e.stopPropagation() // Prevent row click from triggering

    setConfirmDialog({
      isOpen: true,
      data: {
        establishment,
        onConfirm: async () => {
          setConfirmDialog({ isOpen: false, data: null })
          await performRestore(establishment)
        },
        title: 'Restore Establishment',
        message: `Restore "${establishment.trade_name}" to active status?`,
        confirmText: 'Restore',
        confirmStyle: 'primary'
      }
    })
  }

  const performRestore = async (establishment) => {
    try{
      const { error } = await supabase
        .from('establishments')
        .update({ active: true })
        .eq('id', establishment.id)

      if (error) throw error

      logAuditEvent(AUDIT_ACTIONS.ESTABLISHMENT_RESTORED, {
        establishment_id: establishment.id,
        mxb_number: establishment.mxb_number,
        trade_name: establishment.trade_name
      }).catch(() => {})
      toast.success(`"${establishment.trade_name}" has been restored to active status.`)
      queryClient.invalidateQueries({ queryKey: ['establishments'] })
    } catch (error) {
      console.error('Error restoring establishment:', error)
      toast.error('Failed to restore establishment: ' + error.message)
    }
  }

  // eslint-disable-next-line no-unused-vars
  const handleDeletePermanently = (establishment, e) => {
    e.stopPropagation() // Prevent row click from triggering

    // First show warning confirmation
    setConfirmDialog({
      isOpen: true,
      data: {
        establishment,
        onConfirm: () => {
          setConfirmDialog({ isOpen: false, data: null })
          // Then show prompt for name confirmation
          setPromptDialog({
            isOpen: true,
            data: {
              establishment,
              onConfirm: async (inputValue) => {
                setPromptDialog({ isOpen: false, data: null })
                if (inputValue === establishment.trade_name) {
                  await performDelete(establishment)
                } else {
                  toast.error('Name did not match. Deletion cancelled.')
                }
              }
            }
          })
        },
        title: '⚠️ PERMANENT DELETE WARNING',
        message: `Are you absolutely sure you want to PERMANENTLY delete "${establishment.trade_name}"?\n\nThis will delete:\n- The establishment record\n- ALL ${establishment.inspections?.length || 0} inspection records\n- ALL associated data\n\nTHIS CANNOT BE UNDONE!`,
        confirmText: 'Continue to Confirm',
        confirmStyle: 'danger'
      }
    })
  }

  const performDelete = async (establishment) => {
    try {
      // Delete all inspections first (due to foreign key constraints)
      const { error: inspectionsError } = await supabase
        .from('inspections')
        .delete()
        .eq('establishment_id', establishment.id)

      if (inspectionsError) throw inspectionsError

      // Then delete the establishment
      const { error: establishmentError } = await supabase
        .from('establishments')
        .delete()
        .eq('id', establishment.id)

      if (establishmentError) throw establishmentError

      toast.success(`"${establishment.trade_name}" and all associated data have been permanently deleted.`)
      queryClient.invalidateQueries({ queryKey: ['establishments'] })
    } catch (error) {
      console.error('Error deleting establishment:', error)
      toast.error('Failed to delete establishment: ' + error.message)
    }
  }

  const getStatusBadge = (status, isNewOpeningOnly = false) => {
    const labels = {
      current: 'Current',
      due_soon: 'Due Soon',
      overdue: 'Overdue',
      never: 'Never'
    }

    // Map status to Badge variant
    const variantMap = {
      current: 'current',
      due_soon: 'dueSoon',
      overdue: 'overdue',
      never: 'never'
    }

    return (
      <div style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}>
        <Badge variant={variantMap[status] || 'never'} size={isModernLayout ? 'md' : 'sm'}>
          {labels[status]}
        </Badge>
        {isNewOpeningOnly && (
          <span
            title="New Opening - No physical inspection yet"
            style={{
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              cursor: 'help'
            }}
          >
            <Building2 size={14} />
          </span>
        )}
      </div>
    )
  }

  const getRowBackgroundColor = (status) => {
    if (isModernLayout) {
      return 'transparent'
    }
    if (darkMode) {
      return colors.bg.card
    }

    // Classic layout: use status-based colors
    const rowColors = {
      current: '#ecfdf5',
      due_soon: '#fefce8',
      overdue: '#fef2f2',
      never: '#f9fafb'
    }
    return rowColors[status] || rowColors.never
  }

  const getStatusBorderColor = (status) => {
    const borderColors = {
      current: '#10b981',    // Green
      due_soon: '#f59e0b',   // Orange
      overdue: '#ef4444',    // Red
      never: '#9ca3af'       // Gray
    }
    return borderColors[status] || borderColors.never
  }

  // Pagination calculations
  const totalPages = Math.ceil(filteredEstablishments.length / RECORDS_PER_PAGE)
  const startIndex = (currentPage - 1) * RECORDS_PER_PAGE
  const endIndex = startIndex + RECORDS_PER_PAGE
  const currentEstablishments = filteredEstablishments.slice(startIndex, endIndex)

  const goToPage = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const PaginationControls = () => {
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    const pages = []
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    if (isModernLayout) {
      const pgBtn = (disabled, onClick, children) => (
        <button
          onClick={onClick}
          disabled={disabled}
          style={{
            minWidth: '30px',
            height: '30px',
            padding: '0 8px',
            background: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)',
            border: `1px solid ${mRowBorder}`,
            borderRadius: '6px',
            color: disabled ? (darkMode ? 'rgba(74,96,128,0.4)' : 'rgba(74,96,128,0.35)') : mSecondary,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 120ms ease'
          }}
          onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = darkMode ? 'rgba(30,54,90,0.8)' : 'rgba(255,255,255,0.9)'; e.currentTarget.style.color = mPrimary }}}
          onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = mSecondary }}}
        >
          {children}
        </button>
      )

      return (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 18px',
          borderTop: `1px solid ${isModernLayout ? mRowBorder : colors.border.primary}`
        }}>
          <span style={{ fontSize: '12px', color: isModernLayout ? mMuted : colors.text.secondary, letterSpacing: '0.01em' }}>
            Showing {startIndex + 1}–{Math.min(endIndex, filteredEstablishments.length)} of {filteredEstablishments.length} establishments
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {pgBtn(currentPage === 1, () => goToPage(currentPage - 1), '‹')}
            {startPage > 1 && <span style={{ fontSize: '12px', color: mMuted, padding: '0 2px' }}>…</span>}
            {pages.map(page => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                style={{
                  minWidth: '30px',
                  height: '30px',
                  padding: '0 8px',
                  background: currentPage === page ? mAccent : (darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)'),
                  border: currentPage === page ? `1px solid ${mAccent}` : `1px solid ${mRowBorder}`,
                  borderRadius: '6px',
                  color: currentPage === page ? 'white' : mSecondary,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: currentPage === page ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 120ms ease'
                }}
              >
                {page}
              </button>
            ))}
            {endPage < totalPages && <span style={{ fontSize: '12px', color: mMuted, padding: '0 2px' }}>…</span>}
            {pgBtn(currentPage === totalPages, () => goToPage(currentPage + 1), '›')}
          </div>
        </div>
      )
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: '1.5rem',
        padding: '1rem',
        backgroundColor: colors.bg.card,
        borderRadius: '8px'
      }}>
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: currentPage === 1 ? colors.bg.secondary : colors.bg.card,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          First
        </button>

        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: currentPage === 1 ? colors.bg.secondary : colors.bg.card,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Previous
        </button>

        {startPage > 1 && <span style={{ color: colors.text.tertiary }}>...</span>}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: currentPage === page ? '#2563eb' : colors.bg.card,
              color: currentPage === page ? 'white' : colors.text.primary,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: currentPage === page ? '500' : 'normal'
            }}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && <span style={{ color: colors.text.tertiary }}>...</span>}

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: currentPage === totalPages ? colors.bg.secondary : colors.bg.card,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '4px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Next
        </button>

        <button
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: currentPage === totalPages ? colors.bg.secondary : colors.bg.card,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '4px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Last
        </button>

        <span style={{
          marginLeft: '1rem',
          color: colors.text.tertiary,
          fontSize: '0.875rem'
        }}>
          Page {currentPage} of {totalPages}
          ({startIndex + 1}-{Math.min(endIndex, filteredEstablishments.length)} of {filteredEstablishments.length})
        </span>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div style={{ padding: '1rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>
            Loading Establishments...
          </h2>
        </div>
        <TableSkeleton rows={10} columns={9} />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#dc2626' }}>
        Error: {error.message}
      </div>
    )
  }

  // Count active filters (excluding the default agent filter)
  const activeFiltersCount = [
    searchTerm,
    cityFilter,
    statusFilter,
    // Only count agentFilter if it's not the user's default
    // Super admins: default is '' (all agents), don't count
    // Regular users: default is their name, don't count
    (agentFilter &&
     agentFilter !== currentUserName &&
     !(currentUserRole === 'super_admin' && agentFilter === ''))
      ? agentFilter : ''
  ].filter(Boolean).length

  // ---------- Phones: compact toolbar and rows ----------
  const mText2 = darkMode ? '#a9bdd2' : '#4a6178'
  const STATUS_LABEL = { overdue: 'Overdue', due_soon: 'Due soon', never: 'Never', current: 'Current' }
  const STATUS_TEXT = darkMode
    ? { overdue: '#fca5a5', due_soon: '#fcd34d', never: '#a9bdd2', current: '#86efac' }
    : { overdue: '#b91c1c', due_soon: '#a16207', never: '#4a6178', current: '#15803d' }
  const chip = (active) => ({
    flexShrink: 0, minHeight: '40px', padding: '0 14px', borderRadius: '9999px', cursor: 'pointer',
    border: `1px solid ${active ? mAccent : mRowBorder}`, background: active ? mAccent : (darkMode ? 'rgba(125,170,218,0.06)' : '#ffffff'),
    color: active ? '#ffffff' : mPrimary, fontSize: '14px', fontWeight: '600', fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
  })
  const iconBtn = { flexShrink: 0, width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }

  const mobileToolbar = isMobile && (
    <div style={{ position: 'sticky', top: 0, zIndex: 20, background: mGlassBg, borderBottom: `1px solid ${mRowBorder}`, borderRadius: '8px 8px 0 0', padding: '12px 12px 10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search name, permit # or street"
          aria-label="Search establishments"
          style={{ flex: 1, minWidth: 0, height: '48px', padding: '0 14px', borderRadius: '12px', border: `1px solid ${mRowBorder}`, background: darkMode ? 'rgba(125,170,218,0.06)' : '#ffffff', color: mPrimary, fontFamily: 'inherit' }}
        />
        <button type="button" onClick={() => setViewMode(viewMode === 'map' ? 'cards' : 'map')} aria-label={viewMode === 'map' ? 'Show list' : 'Show map'}
          style={{ ...iconBtn, border: `1px solid ${mRowBorder}`, background: darkMode ? 'rgba(125,170,218,0.06)' : '#ffffff', color: mAccent }}>
          {viewMode === 'map' ? <List size={20} /> : <Map size={20} />}
        </button>
        <button type="button" onClick={() => setShowNewForm(true)} aria-label="Add establishment"
          style={{ ...iconBtn, border: 'none', background: mAccent, color: '#ffffff', fontSize: '26px', lineHeight: 1 }}>+</button>
      </div>
      {/* Row 2: permit type and Filters */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {[{ key: 'mxb', label: 'Mixed Beverage' }, { key: 'beer_wine', label: 'Beer/Wine' }].map(({ key, label }) => (
          <button key={key} type="button" style={chip(establishmentType === key)}
            onClick={() => { setEstablishmentType(key); setCurrentPage(1); setStatusFilter('') }}>{label}</button>
        ))}
        <button type="button" style={{ ...chip(showMobileFilters || !!agentFilter || routePlanningMode), marginLeft: 'auto' }} aria-expanded={showMobileFilters}
          onClick={() => setShowMobileFilters(v => !v)}>
          Filters{agentFilter ? ' · 1' : ''}
        </button>
      </div>
      {showMobileFilters && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '2px' }}>
          <select value={agentFilter} onChange={e => { setAgentFilter(e.target.value); setHasUserChangedFilter(true) }} aria-label="Agent"
            style={{ height: '48px', padding: '0 12px', borderRadius: '12px', border: `1px solid ${mRowBorder}`, background: darkMode ? 'rgba(125,170,218,0.06)' : '#ffffff', color: mPrimary, fontFamily: 'inherit' }}>
            <option value="">All agents</option>
            <option value="__unassigned__">Unassigned</option>
            {agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {organization?.features?.route_planning && (
              <button type="button" onClick={() => { setRoutePlanningMode(m => !m); setSelectedForRoute(new Set()) }} style={chip(routePlanningMode)}>
                {routePlanningMode ? 'Cancel route' : 'Plan route'}
              </button>
            )}
            {activeFiltersCount > 0 && (
              <button type="button" onClick={clearFilters} style={chip(false)}>Clear filters ({activeFiltersCount})</button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  // Phones: never-checked establishments first, then the longest since last checked
  // (priority establishments stay at the top, as everywhere else)
  const lastCheckedTime = est => {
    const ins = est.inspections || []
    return ins.length ? Math.max(...ins.map(i => new Date(i.inspection_date).getTime())) : -Infinity
  }
  const mobileShown = isMobile
    ? [...filteredEstablishments].sort((a, b) =>
        (b.priority_inspection ? 1 : 0) - (a.priority_inspection ? 1 : 0) ||
        lastCheckedTime(a) - lastCheckedTime(b) ||
        (a.trade_name || '').localeCompare(b.trade_name || '')
      ).slice(0, mobileVisible)
    : []
  const mobileList = isMobile && (
    <div>
      <div style={{ padding: '10px 14px 6px', fontSize: '13px', color: mText2 }}>
        {filteredEstablishments.length} establishment{filteredEstablishments.length === 1 ? '' : 's'}
        {routePlanningMode ? ` · ${selectedForRoute.size} selected for the route` : ''}
      </div>
      {mobileShown.map(est => {
        const { date, status } = getLastInspectionDate(est.inspections, est.risk_level)
        const selected = selectedForRoute.has(est.id)
        return (
          <button
            key={est.id}
            type="button"
            onClick={e => routePlanningMode ? toggleRouteSelection(est.id, e) : setSelectedEstablishment(est)}
            aria-pressed={routePlanningMode ? selected : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: '72px',
              padding: '10px 14px', border: 'none', borderTop: `1px solid ${mRowBorder}`,
              background: selected ? (darkMode ? 'rgba(13,148,136,0.15)' : 'rgba(13,148,136,0.07)') : 'transparent',
              textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: mPrimary
            }}
          >
            {routePlanningMode && (
              <span aria-hidden="true" style={{ flexShrink: 0, width: '24px', height: '24px', borderRadius: '6px', border: `2px solid ${selected ? '#0d9488' : mRowBorder}`, background: selected ? '#0d9488' : 'transparent', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>{selected ? '✓' : ''}</span>
            )}
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: '16px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{est.trade_name}</span>
                {est.priority_inspection && (
                  <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: '700', color: darkMode ? '#fcd34d' : '#a16207', border: `1px solid ${darkMode ? 'rgba(252,211,77,0.4)' : 'rgba(161,98,7,0.35)'}`, borderRadius: '6px', padding: '1px 6px' }}>PRIORITY</span>
                )}
                <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: STATUS_TEXT[status] }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: getStatusBorderColor(status) }} />
                  {STATUS_LABEL[status]}
                </span>
              </span>
              <span style={{ fontSize: '13px', color: mText2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: '12px' }}>{est.mxb_number}</span>
                {' · '}{est.address}{est.city ? `, ${est.city}` : ''}
              </span>
              <span style={{ fontSize: '13px', color: mText2 }}>
                {est.users ? `${est.users.first_name} ${est.users.last_name}` : 'Unassigned'} · Last checked: {date === 'Never' ? 'never' : date}
              </span>
            </span>
          </button>
        )
      })}
      {filteredEstablishments.length === 0 && (
        <p style={{ textAlign: 'center', margin: '2rem 1rem', color: mText2 }}>No establishments match your filters.</p>
      )}
      {filteredEstablishments.length > mobileVisible && (
        <div style={{ padding: '12px 14px 16px', borderTop: `1px solid ${mRowBorder}` }}>
          <button type="button" onClick={() => setMobileVisible(v => v + MOBILE_PAGE)}
            style={{ width: '100%', minHeight: '48px', borderRadius: '12px', border: `1px solid ${mRowBorder}`, background: 'transparent', color: mAccent, fontSize: '15px', fontWeight: '600', fontFamily: 'inherit', cursor: 'pointer' }}>
            Show more ({filteredEstablishments.length - mobileVisible} left)
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div style={{ padding: isModernLayout ? (isMobile ? '0.75rem' : '1.25rem') : '1rem', background: 'transparent' }}>

      {/* Unified glass card wraps toolbar + table in modern layout */}
      <div style={isModernLayout ? {
        backgroundColor: mGlassBg,
        border: `1px solid ${mGlassBorder}`,
        borderRadius: '8px',
        boxShadow: mGlassShadow,
        overflow: isMobile ? 'visible' : 'hidden'
      } : {}}>

      {isMobile && mobileToolbar}

      {!isMobile && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isModernLayout ? '0' : '1rem', flexWrap: 'wrap', gap: '0.5rem', padding: isModernLayout ? '14px 18px' : '0' }}>
        {/* Establishment Type Tabs - Left */}
        <div style={{
          display: 'flex',
          gap: '0',
          backgroundColor: mTheadBg,
          borderRadius: '9999px',
          padding: '0.25rem',
          border: `1px solid ${mRowBorder}`,
          width: 'fit-content'
        }}>
          {[
            { key: 'mxb', label: 'Mixed Beverage' },
            { key: 'beer_wine', label: 'Beer/Wine' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setEstablishmentType(key); setCurrentPage(1); setStatusFilter('') }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: establishmentType === key ? mAccent : 'transparent',
                color: establishmentType === key ? 'white' : mMuted,
                border: 'none',
                borderRadius: '9999px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                transition: 'all 0.15s ease',
                boxShadow: establishmentType === key ? '0 2px 8px rgba(61,100,148,0.3)' : 'none'
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Action Buttons - Right */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Inline filters — modern layout only */}
          {isModernLayout && (
            <>
              <select
                value={agentFilter}
                onChange={(e) => { setAgentFilter(e.target.value); setHasUserChangedFilter(true) }}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: `1px solid ${mRowBorder}`,
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  outline: 'none',
                  backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)',
                  color: mSecondary,
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
              >
                <option value="">All Agents</option>
                <option value="__unassigned__">Unassigned</option>
                {agents.map(agent => (
                  <option key={agent} value={agent}>{agent}{agent === currentUserName ? ' (Me)' : ''}</option>
                ))}
              </select>
              {establishmentType === 'mxb' && (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '0.4rem 0.75rem',
                    border: `1px solid ${mRowBorder}`,
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)',
                    color: mSecondary,
                    cursor: 'pointer',
                    minWidth: '130px'
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="current">Current</option>
                  <option value="due_soon">Due Soon</option>
                  <option value="overdue">Overdue</option>
                  <option value="never">Never Inspected</option>
                </select>
              )}
              <input
                type="text"
                placeholder={establishmentType === 'mxb' ? 'Search name, MXB…' : 'Search name, file #…'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: `1px solid ${mRowBorder}`,
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  outline: 'none',
                  backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.6)',
                  color: mSecondary,
                  minWidth: '180px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = mAccent
                  e.target.style.boxShadow = '0 0 0 3px rgba(61,100,148,0.1)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = mRowBorder
                  e.target.style.boxShadow = 'none'
                }}
              />
            </>
          )}

          {/* View Toggle - Desktop: Table/Map, Mobile: Cards/Map */}
          <div style={{
            display: 'flex',
            backgroundColor: mTheadBg,
            borderRadius: isModernLayout ? '8px' : '9999px',
            padding: '0.25rem',
            border: `1px solid ${mRowBorder}`,
            gap: '2px'
          }}>
            {[
              { mode: isMobile ? 'cards' : 'table', Icon: isMobile ? LayoutGrid : List, label: isMobile ? 'Cards' : 'Table' },
              { mode: 'map', Icon: Map, label: 'Map' }
            ].map(({ mode, Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                title={label}
                style={{
                  padding: isModernLayout ? '0.4rem 0.6rem' : '0.5rem 0.875rem',
                  backgroundColor: viewMode === mode ? mAccent : 'transparent',
                  color: viewMode === mode ? 'white' : mMuted,
                  border: 'none',
                  borderRadius: isModernLayout ? '6px' : '9999px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  boxShadow: viewMode === mode ? '0 2px 8px rgba(61,100,148,0.3)' : 'none'
                }}
              >
                {isModernLayout ? <Icon size={15} /> : label === 'Map' ? <><Map size={14} /> Map</> : isMobile ? <><LayoutGrid size={14} /> List</> : <><List size={14} /> Table</>}
              </button>
            ))}
          </div>

          {/* Route Planning Button */}
          {organization?.features?.route_planning && (
            <button
              onClick={() => { setRoutePlanningMode(m => !m); setSelectedForRoute(new Set()) }}
              style={{
                padding: isModernLayout ? '0.4rem 0.875rem' : '0.5rem 1rem',
                backgroundColor: routePlanningMode ? (darkMode ? 'rgba(212,80,80,0.18)' : 'rgba(185,28,28,0.1)') : (darkMode ? 'rgba(90,127,173,0.15)' : 'rgba(61,100,148,0.08)'),
                color: routePlanningMode ? (darkMode ? '#f87171' : '#b91c1c') : mAccent,
                border: `1px solid ${routePlanningMode ? (darkMode ? 'rgba(212,80,80,0.3)' : 'rgba(185,28,28,0.2)') : mRowBorder}`,
                borderRadius: isModernLayout ? '8px' : '9999px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease'
              }}
            >
              {routePlanningMode ? '✕ Cancel Route' : '🗺 Plan Route'}
            </button>
          )}

          {/* Add Establishment Button */}
          <button
            onClick={() => setShowNewForm(true)}
            style={{
              padding: isModernLayout ? '0.4rem 0.875rem' : '0.5rem 1rem',
              backgroundColor: mAccent,
              color: 'white',
              border: 'none',
              borderRadius: isModernLayout ? '8px' : '9999px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
              boxShadow: '0 2px 8px rgba(61,100,148,0.3)'
            }}
          >
            {isModernLayout ? '+ Add' : (isMobile ? '+ Add' : '+ Add Establishment')}
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                padding: isModernLayout ? '0.4rem 0.875rem' : '0.5rem 1rem',
                backgroundColor: darkMode ? 'rgba(212,80,80,0.12)' : 'rgba(138,42,42,0.1)',
                color: darkMode ? '#d45050' : '#8a2a2a',
                border: `1px solid ${darkMode ? 'rgba(212,80,80,0.2)' : 'rgba(138,42,42,0.15)'}`,
                borderRadius: isModernLayout ? '8px' : '9999px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)' }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}
            >
              Clear ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>}

      {/* Active Filter Tags (phones show active filters on the chips instead) */}
      {activeFiltersCount > 0 && !isMobile && (() => {
        const tagStyle = isModernLayout ? {
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px',
          background: mAgentBg,
          border: `1px solid ${mRowBorder}`,
          borderRadius: '20px',
          fontSize: '13px',
          fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
          fontWeight: '500',
          color: mAccent
        } : {
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.375rem 0.75rem',
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontWeight: '500'
        }
        const xStyle = isModernLayout ? {
          background: 'none', border: 'none', color: mAccent,
          cursor: 'pointer', padding: '0', fontSize: '14px',
          lineHeight: 1, display: 'flex', alignItems: 'center', opacity: 0.7
        } : {
          background: 'none', border: 'none', color: '#1e40af',
          cursor: 'pointer', padding: '0', fontSize: '1rem',
          lineHeight: '1', display: 'flex', alignItems: 'center'
        }
        return (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: isModernLayout ? '0' : '1rem',
          padding: isModernLayout ? '8px 14px' : '0',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: isModernLayout ? '11px' : '0.875rem',
            color: isModernLayout ? mMuted : colors.text.tertiary,
            fontWeight: '600',
            letterSpacing: isModernLayout ? '0.06em' : undefined,
            textTransform: isModernLayout ? 'uppercase' : undefined,
            fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : undefined
          }}>
            Filters:
          </span>

          {searchTerm && (
            <div style={tagStyle}>
              <span>Search: "{searchTerm}"</span>
              <button onClick={() => setSearchTerm('')} style={xStyle} title="Remove filter">×</button>
            </div>
          )}

          {cityFilter && (
            <div style={tagStyle}>
              <span>City: {cityFilter}</span>
              <button onClick={() => setCityFilter('')} style={xStyle} title="Remove filter">×</button>
            </div>
          )}

          {agentFilter === '__unassigned__' && (
            <div style={isModernLayout ? { ...tagStyle, background: darkMode ? 'rgba(212,160,32,0.12)' : 'rgba(138,98,0,0.1)', color: darkMode ? '#d4a020' : '#8a6200', border: `1px solid ${darkMode ? 'rgba(212,160,32,0.2)' : 'rgba(138,98,0,0.15)'}` } : { ...tagStyle, backgroundColor: '#fef3c7', color: '#92400e' }}>
              <span>Unassigned</span>
              <button onClick={() => setAgentFilter('')} style={{ ...xStyle, color: isModernLayout ? (darkMode ? '#d4a020' : '#8a6200') : '#92400e' }} title="Remove filter">×</button>
            </div>
          )}

          {agentFilter && agentFilter !== currentUserName && agentFilter !== '__unassigned__' && (
            <div style={tagStyle}>
              <span>Agent: {agentFilter}</span>
              <button
                onClick={() => {
                  if (currentUserRole === 'super_admin' || currentUserRole === 'admin') {
                    setAgentFilter('')
                  } else if (currentUserName) {
                    setAgentFilter(currentUserName)
                  } else {
                    setAgentFilter('')
                  }
                }}
                style={xStyle}
                title="Remove filter"
              >×</button>
            </div>
          )}

          {statusFilter && (
            <div style={tagStyle}>
              <span>
                {statusFilter === 'current' ? 'Current' :
                 statusFilter === 'due_soon' ? 'Due Soon' :
                 statusFilter === 'overdue' ? 'Overdue' :
                 statusFilter === 'never' ? 'Never Inspected' : statusFilter}
              </span>
              <button onClick={() => setStatusFilter('')} style={xStyle} title="Remove filter">×</button>
            </div>
          )}

          <button
            onClick={clearFilters}
            style={isModernLayout ? {
              padding: '4px 10px',
              background: darkMode ? 'rgba(212,80,80,0.12)' : 'rgba(138,42,42,0.1)',
              color: darkMode ? '#d45050' : '#8a2a2a',
              border: `1px solid ${darkMode ? 'rgba(212,80,80,0.2)' : 'rgba(138,42,42,0.15)'}`,
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '13px',
              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
              fontWeight: '500'
            } : {
              padding: '0.375rem 0.75rem',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginLeft: '0.5rem'
            }}
          >
            Clear All
          </button>
        </div>
        )
      })()}

      {/* Thin divider between toolbar and table in modern layout */}
      {isModernLayout && (
        <div style={{ borderBottom: `1px solid ${mRowBorder}` }} />
      )}

      {/* Route Planning Panel */}
      {routePlanningMode && (
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${mRowBorder}`,
          backgroundColor: darkMode ? 'rgba(13,148,136,0.07)' : 'rgba(13,148,136,0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: darkMode ? '#2dd4bf' : '#0d9488', whiteSpace: 'nowrap' }}>
            🗺 {selectedForRoute.size} stop{selectedForRoute.size !== 1 ? 's' : ''} selected
          </span>
          <input
            type="text"
            placeholder="Start address *"
            value={routeStart}
            onChange={e => setRouteStart(e.target.value)}
            style={{
              flex: 1, minWidth: '160px', maxWidth: '260px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: `1px solid ${darkMode ? 'rgba(20,184,166,0.3)' : 'rgba(13,148,136,0.25)'}`,
              background: darkMode ? 'rgba(20,36,58,0.8)' : 'white',
              color: darkMode ? '#c8daf5' : '#1a2a3e',
              fontSize: '13px',
              outline: 'none'
            }}
          />
          <input
            type="text"
            placeholder="End address (optional)"
            value={routeEnd}
            onChange={e => setRouteEnd(e.target.value)}
            style={{
              flex: 1, minWidth: '160px', maxWidth: '260px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: `1px solid ${darkMode ? 'rgba(20,184,166,0.3)' : 'rgba(13,148,136,0.25)'}`,
              background: darkMode ? 'rgba(20,36,58,0.8)' : 'white',
              color: darkMode ? '#c8daf5' : '#1a2a3e',
              fontSize: '13px',
              outline: 'none'
            }}
          />
          <button
            onClick={openRoute}
            disabled={isCalculatingRoute || selectedForRoute.size === 0}
            style={{
              padding: '6px 16px',
              backgroundColor: selectedForRoute.size === 0 ? (darkMode ? 'rgba(20,184,166,0.1)' : 'rgba(13,148,136,0.08)') : (darkMode ? '#0d9488' : '#0d9488'),
              color: selectedForRoute.size === 0 ? (darkMode ? '#2dd4bf' : '#0d9488') : 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: selectedForRoute.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease'
            }}
          >
            {isCalculatingRoute ? 'Opening…' : 'Get Route →'}
          </button>
          <span style={{ fontSize: '12px', color: mMuted }}>Click rows to select stops</span>
        </div>
      )}

      {/* Search and Filter Bar — hidden in modern layout */}
      <div style={{
        display: isModernLayout ? 'none' : 'block',
        backgroundColor: darkMode ? 'rgba(20,36,58,0.7)' : 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding: 'clamp(1rem, 2vw, 1.5rem)',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(30,58,100,0.08)',
        marginBottom: '1rem',
        border: `1px solid ${mRowBorder}`
      }}>
        <style>
          {`
            @media (max-width: 768px) {
              .filter-grid {
                display: flex !important;
                flex-direction: column !important;
                gap: 0.75rem !important;
              }
              .filter-grid > div {
                width: 100% !important;
              }
              .filter-grid select,
              .filter-grid input {
                min-height: 44px !important;
                font-size: 16px !important;
              }
              .search-field-mobile {
                order: -1 !important;
              }
            }
          `}
        </style>
        <div className="filter-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'clamp(0.75rem, 2vw, 1rem)'
        }}>
          {/* Agent Filter */}
          <div>
            <label style={{
              display: 'block', marginBottom: '0.5rem',
              fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: mMuted, fontWeight: '700',
              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
            }}>
              Agent {agentFilter === currentUserName && '(Mine)'}
            </label>
            <select
              value={agentFilter}
              onChange={(e) => { setAgentFilter(e.target.value); setHasUserChangedFilter(true) }}
              style={{
                width: '100%', padding: '0.625rem 0.75rem',
                border: `1px solid rgba(61,100,148,${agentFilter === currentUserName ? '0.4' : '0.18'})`,
                borderRadius: '9999px', fontSize: '0.9rem', transition: 'all 0.2s', outline: 'none',
                backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.7)',
                color: mPrimary, cursor: 'pointer',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
              }}
              onFocus={(e) => { e.target.style.borderColor = mAccent; e.target.style.boxShadow = '0 0 0 3px rgba(61,100,148,0.1)' }}
              onBlur={(e) => { e.target.style.borderColor = `rgba(61,100,148,${agentFilter === currentUserName ? '0.4' : '0.18'})`; e.target.style.boxShadow = 'none' }}
            >
              <option value="">All Agents</option>
              <option value="__unassigned__">Unassigned</option>
              {agents.map(agent => (
                <option key={agent} value={agent}>{agent}{agent === currentUserName ? ' (Me)' : ''}</option>
              ))}
            </select>
          </div>

          {/* Status Filter - Only show for MXB establishments */}
          {establishmentType === 'mxb' && (
            <div>
              <label style={{
                display: 'block', marginBottom: '0.5rem',
                fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase',
                color: mMuted, fontWeight: '700',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
              }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%', padding: '0.625rem 0.75rem',
                  border: `1px solid ${mRowBorder}`, borderRadius: '9999px',
                  fontSize: '0.9rem', transition: 'all 0.2s', outline: 'none',
                  backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.7)',
                  color: mPrimary, cursor: 'pointer',
                  fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
                }}
                onFocus={(e) => { e.target.style.borderColor = mAccent; e.target.style.boxShadow = '0 0 0 3px rgba(61,100,148,0.1)' }}
                onBlur={(e) => { e.target.style.borderColor = mRowBorder; e.target.style.boxShadow = 'none' }}
              >
                <option value="">All Status</option>
                <option value="current">Current</option>
                <option value="due_soon">Due Soon</option>
                <option value="overdue">Overdue</option>
                <option value="never">Never Inspected</option>
              </select>
            </div>
          )}

          {/* City Filter */}
          <div>
            <label style={{
              display: 'block', marginBottom: '0.5rem',
              fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: mMuted, fontWeight: '700',
              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
            }}>
              City
            </label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              style={{
                width: '100%', padding: '0.625rem 0.75rem',
                border: `1px solid ${mRowBorder}`, borderRadius: '9999px',
                fontSize: '0.9rem', transition: 'all 0.2s', outline: 'none',
                backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.7)',
                color: mPrimary, cursor: 'pointer',
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
              }}
              onFocus={(e) => { e.target.style.borderColor = mAccent; e.target.style.boxShadow = '0 0 0 3px rgba(61,100,148,0.1)' }}
              onBlur={(e) => { e.target.style.borderColor = mRowBorder; e.target.style.boxShadow = 'none' }}
            >
              <option value="">All Cities</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div className="search-field-mobile">
            <label style={{
              display: 'block', marginBottom: '0.5rem',
              fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase',
              color: mMuted, fontWeight: '700',
              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
            }}>
              Search
            </label>
            <input
              type="text"
              placeholder={establishmentType === 'mxb' ? "Name, MXB, Address..." : "Name, File #, Address..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '0.625rem 0.75rem',
                border: `1px solid ${mRowBorder}`, borderRadius: '9999px',
                fontSize: '0.9rem', transition: 'all 0.2s', outline: 'none',
                backgroundColor: darkMode ? 'rgba(20,36,58,0.6)' : 'rgba(255,255,255,0.7)',
                color: mPrimary,
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif"
              }}
              onFocus={(e) => { e.target.style.borderColor = mAccent; e.target.style.boxShadow = '0 0 0 3px rgba(61,100,148,0.1)' }}
              onBlur={(e) => { e.target.style.borderColor = mRowBorder; e.target.style.boxShadow = 'none' }}
            />
          </div>
        </div>
      </div>
      
      {/* Map View */}
      {viewMode === 'map' ? (
        <div style={{
          backgroundColor: isModernLayout ? mGlassBg : colors.bg.card,
          backdropFilter: isModernLayout ? 'blur(14px) saturate(1.3)' : undefined,
          WebkitBackdropFilter: isModernLayout ? 'blur(14px) saturate(1.3)' : undefined,
          borderRadius: isModernLayout ? '12px' : '8px',
          boxShadow: isModernLayout ? mGlassShadow : '0 1px 3px rgba(0,0,0,0.1)',
          border: isModernLayout ? `1px solid ${mGlassBorder}` : undefined,
          overflow: 'hidden',
          padding: '1rem'
        }}>
          <MapView
            establishments={filteredEstablishments}
            onSelectEstablishment={setSelectedEstablishment}
            establishmentType={establishmentType}
          />
        </div>
      ) : isMobile ? mobileList : viewMode === 'cards' ? (
        <>
          {/* Card View */}
          <div>
            {currentEstablishments.map((est) => (
              <div key={est.id} style={{ position: 'relative' }}
                onClick={routePlanningMode ? (e) => toggleRouteSelection(est.id, e) : undefined}
              >
                {routePlanningMode && (
                  <div style={{
                    position: 'absolute', top: '12px', right: '12px', zIndex: 10,
                    width: '26px', height: '26px', borderRadius: '6px',
                    border: `2px solid ${selectedForRoute.has(est.id) ? '#0d9488' : 'rgba(100,130,160,0.4)'}`,
                    backgroundColor: selectedForRoute.has(est.id) ? '#0d9488' : (darkMode ? 'rgba(20,36,58,0.8)' : 'rgba(255,255,255,0.9)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                  }}>
                    {selectedForRoute.has(est.id) && <span style={{ color: 'white', fontSize: '14px', lineHeight: 1 }}>✓</span>}
                  </div>
                )}
                <div style={{ opacity: routePlanningMode && !selectedForRoute.has(est.id) ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                  <EstablishmentCard
                    establishment={est}
                    onSelect={routePlanningMode ? () => {} : setSelectedEstablishment}
                    getStatusBadge={getStatusBadge}
                    getLastInspectionDate={getLastInspectionDate}
                    establishmentType={establishmentType}
                  />
                </div>
              </div>
            ))}

            {filteredEstablishments.length === 0 && (
              <p style={{ textAlign: 'center', marginTop: '2rem', color: colors.text.tertiary }}>
                No establishments match your filters.
              </p>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && <PaginationControls />}
          </div>
        </>
      ) : (
        <>
          {/* Table View */}
          <div style={{
        backgroundColor: isModernLayout ? 'transparent' : colors.bg.card,
        boxShadow: isModernLayout ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: isModernLayout ? '0' : '8px',
        overflow: 'visible'
      }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: 'calc(100vh - 160px)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead style={{
            backgroundColor: darkMode ? '#0f1e30' : '#f0f4f8',
            borderBottom: isModernLayout ? `1px solid ${mRowBorder}` : undefined,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}>
            {(() => {
              const thStyle = {
                padding: isModernLayout ? '7px 10px' : '6px 10px',
                textAlign: isModernLayout ? 'left' : 'center',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: '600',
                fontSize: isModernLayout ? '13px' : undefined,
                letterSpacing: isModernLayout ? '0.06em' : undefined,
                textTransform: isModernLayout ? 'uppercase' : undefined,
                whiteSpace: 'nowrap',
                transition: 'color 0.2s',
                color: isModernLayout ? mMuted : colors.text.primary
              }
              const thHoverIn = (e) => { e.currentTarget.style.color = isModernLayout ? mAccent : colors.text.primary }
              const thHoverOut = (e) => { e.currentTarget.style.color = isModernLayout ? mMuted : colors.text.primary }
              const SortIcon = ({ field }) => (
                <span style={{ fontSize: '0.875rem', opacity: sortField === field ? 1 : 0.3 }}>
                  {sortField === field && sortDirection === 'desc' ? '↓' : '↑'}
                </span>
              )
              return (
                <tr>
                  <th onClick={() => handleSort('mxb_number')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      {idLabel}<SortIcon field="mxb_number" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('trade_name')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      Trade Name<SortIcon field="trade_name" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('address')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      Address<SortIcon field="address" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('city')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      City<SortIcon field="city" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('zip')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      Zip<SortIcon field="zip" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('agent')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      Agent<SortIcon field="agent" />
                    </div>
                  </th>
                  {establishmentType === 'mxb' && (
                    <th onClick={() => handleSort('status')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                        Status<SortIcon field="status" />
                      </div>
                    </th>
                  )}
                  {establishmentType === 'mxb' && (
                    <th onClick={() => handleSort('risk')} style={{ ...thStyle, textAlign: 'center', width: '60px' }} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>Risk {sortField === 'risk' && <span style={{ fontSize: '0.875rem' }}>{sortDirection === 'desc' ? '↓' : '↑'}</span>}</th>
                  )}
                  <th onClick={() => handleSort('last_inspection')} style={thStyle} onMouseEnter={thHoverIn} onMouseLeave={thHoverOut}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isModernLayout ? 'flex-start' : 'center', gap: '0.5rem' }}>
                      Last Checked<SortIcon field="last_inspection" />
                    </div>
                  </th>
                  <th style={{ ...thStyle, cursor: 'default', width: isModernLayout ? '68px' : '60px' }}>
                    Actions
                  </th>
                </tr>
              )
            })()}
          </thead>
          <tbody>
            {currentEstablishments.map((est, rowIndex) => {
              const { date, status, isNewOpeningOnly } = getLastInspectionDate(est.inspections, est.risk_level)

              // Use neutral colors for beer_wine establishments (no status tracking)
              const baseRowBgColor = est.priority_inspection
                ? (darkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)')
                : (isModernLayout || establishmentType !== 'beer_wine')
                  ? getRowBackgroundColor(status)
                  : colors.bg.card
              const rowBgColor = baseRowBgColor

              return (
                <tr
                  key={est.id}
                  onClick={() => setSelectedEstablishment(est)}
                  style={{
                    borderBottom: isModernLayout ? `1px solid ${mRowBorder}` : `1px solid ${colors.border.primary}`,
                    borderLeft: est.priority_inspection ? '4px solid #f59e0b' : isModernLayout ? 'none' : `4px solid ${establishmentType === 'beer_wine' ? colors.border.secondary : getStatusBorderColor(status)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: rowBgColor,
                    position: 'relative',
                    zIndex: openActionsDropdown === est.id ? 100 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (isModernLayout) {
                      e.currentTarget.style.backgroundColor = mRowHover
                      e.currentTarget.style.boxShadow = darkMode ? 'inset 3px 0 0 #5a7fad' : 'inset 3px 0 0 #3d6494'
                    } else if (darkMode) {
                      e.currentTarget.style.backgroundColor = colors.bg.hover
                    } else {
                      const hoverColors = {
                        current: '#d1fae5',
                        due_soon: '#fef3c7',
                        overdue: '#fecaca',
                        never: '#f3f4f6'
                      }
                      e.currentTarget.style.backgroundColor = hoverColors[status] || hoverColors.never
                    }
                    if (!isModernLayout) {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'
                      e.currentTarget.style.transform = 'translateX(4px)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = rowBgColor
                    e.currentTarget.style.boxShadow = 'none'
                    e.currentTarget.style.transform = 'none'
                  }}
                >
                  {routePlanningMode && (
                    <td style={{ padding: '9px 8px', width: '36px' }} onClick={e => toggleRouteSelection(est.id, e)}>
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '4px',
                        border: `2px solid ${selectedForRoute.has(est.id) ? '#0d9488' : mRowBorder}`,
                        backgroundColor: selectedForRoute.has(est.id) ? '#0d9488' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', flexShrink: 0
                      }}>
                        {selectedForRoute.has(est.id) && <span style={{ color: 'white', fontSize: '11px', lineHeight: 1 }}>✓</span>}
                      </div>
                    </td>
                  )}
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mAccent : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', fontWeight: isModernLayout ? '500' : 'inherit', whiteSpace: 'nowrap' }}>{est.mxb_number}</td>
                  <td title={est.trade_name} style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mPrimary : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', fontWeight: isModernLayout ? '700' : 'inherit', maxWidth: isModernLayout ? '320px' : undefined, whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{est.trade_name}</span>
                      {est.priority_inspection && (
                        <span title={est.priority_reason || 'Priority Inspection'} style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px', borderRadius: '10px', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', color: '#b45309', fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          ⚑ Priority
                        </span>
                      )}
                    </span>
                  </td>
                  <td title={est.address} style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mSecondary : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', fontWeight: isModernLayout ? '500' : 'inherit', maxWidth: isModernLayout ? '180px' : undefined, overflow: isModernLayout ? 'hidden' : undefined, textOverflow: isModernLayout ? 'ellipsis' : undefined, whiteSpace: 'nowrap' }}>{est.address}</td>
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mSecondary : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', fontWeight: isModernLayout ? '500' : 'inherit', whiteSpace: 'nowrap' }}>{est.city}</td>
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mSecondary : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', fontWeight: isModernLayout ? '500' : 'inherit' }}>{est.zip}</td>
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mPrimary : colors.text.primary, textAlign: 'center' }}>
                    {isModernLayout ? (
                      // Initials in a circle; full name on hover
                      <span
                        title={est.users ? `${est.users.first_name} ${est.users.last_name}` : 'Unassigned'}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%', background: est.users ? mAgentBg : 'transparent', border: est.users ? 'none' : `1px dashed ${mRowBorder}`, fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", fontSize: '12px', fontWeight: '600', letterSpacing: '0.02em', color: est.users ? mAccent : mMuted, cursor: 'default' }}
                      >
                        {est.users ? `${(est.users.first_name || '').charAt(0)}${(est.users.last_name || '').charAt(0)}`.toUpperCase() : '–'}
                      </span>
                    ) : (
                      est.users ? `${est.users.first_name} ${est.users.last_name}` : 'Unassigned'
                    )}
                  </td>
                  {establishmentType === 'mxb' && (
                    <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mPrimary : colors.text.primary, whiteSpace: 'nowrap' }}>{getStatusBadge(status, isNewOpeningOnly)}</td>
                  )}
                  {establishmentType === 'mxb' && (() => {
                    const riskLevel = riskOverrides[est.id] ?? est.risk_level ?? 'low'
                    const riskVariant = riskLevel === 'high' ? 'overdue' : riskLevel === 'medium' ? 'dueSoon' : 'current'
                    const riskText = riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)
                    return (
                      <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', textAlign: 'center' }}>
                        <button onClick={(e) => handleRiskClick(est, e)} title={`${riskText} risk. Click to change`}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                          {/* Same colors as before, shown as a circle with L / M / H */}
                          <Badge variant={riskVariant} size={isModernLayout ? 'md' : 'sm'}
                            style={{ width: '30px', height: '30px', padding: 0, borderRadius: '50%', justifyContent: 'center', fontSize: '12px', fontWeight: '700', letterSpacing: 0 }}>
                            {riskText.charAt(0)}
                          </Badge>
                        </button>
                      </td>
                    )
                  })()}
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', color: isModernLayout ? mMuted : colors.text.primary, fontFamily: isModernLayout ? "Inter, ui-sans-serif, system-ui, sans-serif" : 'inherit', fontWeight: isModernLayout ? '500' : 'inherit', fontSize: isModernLayout ? '16px' : 'inherit', whiteSpace: 'nowrap' }}>{date}</td>
                  <td style={{ padding: isModernLayout ? '13px 10px' : '6px 10px', textAlign: isModernLayout ? 'left' : 'center', position: 'relative', color: isModernLayout ? mMuted : colors.text.primary }}>
                    {isModernLayout ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {[
                          { Icon: Pencil, title: 'Edit', onClick: (e) => { e.stopPropagation(); handleEdit(est, e) } },
                          { Icon: Archive, title: 'Archive', onClick: (e) => { e.stopPropagation(); handleArchive(est, e) } }
                        ].map(({ Icon, title, onClick }) => (
                          <button
                            key={title}
                            onClick={onClick}
                            title={title}
                            style={{
                              width: '26px', height: '26px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'transparent',
                              border: `1px solid ${mRowBorder}`,
                              borderRadius: '5px',
                              cursor: 'pointer',
                              color: mMuted,
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = mRowHover
                              e.currentTarget.style.color = mAccent
                              e.currentTarget.style.borderColor = mAccent
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.color = mMuted
                              e.currentTarget.style.borderColor = mRowBorder
                            }}
                          >
                            <Icon size={12} />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenActionsDropdown(openActionsDropdown === est.id ? null : est.id)
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        padding: '0.25rem 0.4rem',
                        color: colors.text.secondary,
                        borderRadius: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.bg.secondary
                        e.currentTarget.style.color = colors.text.primary
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.color = colors.text.secondary
                      }}
                    >
                      ⚙️
                    </button>
                    {openActionsDropdown === est.id && (
                      <div
                        style={{
                          position: 'absolute',
                          right: '0',
                          top: '100%',
                          backgroundColor: colors.bg.card,
                          border: `1px solid ${colors.border.primary}`,
                          borderRadius: '10px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          zIndex: 1000,
                          minWidth: '160px',
                          overflow: 'hidden'
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenActionsDropdown(null)
                            handleEdit(est, e)
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            backgroundColor: 'transparent',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: colors.text.primary,
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bg.hover}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenActionsDropdown(null)
                            handleArchive(est, e)
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderTop: `1px solid ${colors.border.primary}`,
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#8a2a2a',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(138,42,42,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          🗄️ Archive
                        </button>
                      </div>
                    )}
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && <PaginationControls />}

      {filteredEstablishments.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '2rem', color: colors.text.tertiary }}>
          No establishments match your filters.
        </p>
      )}
      </>
      )}

      </div>

      {selectedEstablishment && (
        <InspectionHistory
          establishment={selectedEstablishment}
          onClose={() => setSelectedEstablishment(null)}
          onNewInspection={onNewInspection}
          onEdit={handleEdit}
          onArchive={handleArchive}
          onPriorityCleared={() => queryClient.invalidateQueries({ queryKey: ['establishments'] })}
        />
      )}

      {showNewForm && (
        <NewEstablishmentForm
          onClose={() => setShowNewForm(false)}
          onSuccess={() => {
            setShowNewForm(false)
            queryClient.invalidateQueries({ queryKey: ['establishments'] })
          }}
          establishmentType={establishmentType}
        />
      )}

      {showEditForm && establishmentToEdit && (
        <EditEstablishmentForm
          establishment={establishmentToEdit}
          onClose={() => {
            setShowEditForm(false)
            setEstablishmentToEdit(null)
          }}
          onSuccess={() => {
            setShowEditForm(false)
            setEstablishmentToEdit(null)
            queryClient.invalidateQueries({ queryKey: ['establishments'] })
          }}
        />
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, data: null })}
        onConfirm={confirmDialog.data?.onConfirm}
        title={confirmDialog.data?.title || 'Archive Establishment'}
        message={confirmDialog.data?.message || `Are you sure you want to archive "${confirmDialog.data?.establishment?.trade_name}"?\n\nThis will hide it from the active list but preserve all inspection history.`}
        confirmText={confirmDialog.data?.confirmText || 'Archive'}
        cancelText="Cancel"
        confirmStyle={confirmDialog.data?.confirmStyle || 'danger'}
      />

      <PromptDialog
        isOpen={promptDialog.isOpen}
        onClose={() => setPromptDialog({ isOpen: false, data: null })}
        onConfirm={promptDialog.data?.onConfirm}
        title="Confirm Permanent Deletion"
        message={`Type "${promptDialog.data?.establishment?.trade_name}" exactly to confirm permanent deletion:`}
        placeholder={promptDialog.data?.establishment?.trade_name}
        confirmText="Delete Permanently"
        cancelText="Cancel"
        expectedValue={promptDialog.data?.establishment?.trade_name}
      />
    </div>
  )
}